import type { JointDef, PlaneKey } from './joints'

/**
 * Das Maschinenmodell — alle Konstruktionswerte als Daten.
 *
 * ZWEI EBENEN, EINE RICHTUNG
 *
 *   Eingaben              abgeleitet
 *   ─────────────────     ──────────────────────────────
 *   Gelenkpunkte     ──►  Segmentlängen
 *   Winkelfreiheiten ──►  Grenzen je Gelenk und Ebene
 *   Einzelmassen     ──►  Gesamtmasse, Massenanteile
 *
 * Nichts davon wird doppelt gepflegt. Eine Segmentlänge, die neben den
 * Gelenkpunkten ein zweites Mal gespeichert ist, läuft beim ersten Verschieben
 * eines Punktes auseinander — und zwar still. Dasselbe gilt für die Gesamtmasse:
 * sie ist die Summe der Einzelmassen, nie eine eigene Zahl.
 *
 * Für den Abgleich mit einer Waage oder einer Zielvorgabe gibt es
 * `massTarget` — aber als VERGLEICHSWERT, nicht als zweite Wahrheit. Die
 * Abweichung wird gerechnet und angezeigt, nicht verrechnet.
 *
 * Keine Zahl steht im Code, jede in einem Datensatz. Ein neuer Auslegungsstand
 * ist ein zweites Objekt, kein Eingriff in eine Formel.
 */

/* -------------------------------------------------------------------------
   Gelenkpunkte
   ------------------------------------------------------------------------- */

/**
 * Ein Punkt im Konstruktionsrahmen, in MILLIMETERN.
 *
 * Nullpunkt ist das grosse Kreuzgelenk des Rumpfaktuators.
 * x nach vorn zur Bauchseite, y nach aussen zur linken Körperseite,
 * z nach oben zum Kopf.
 *
 * Millimeter und nicht Meter, weil so die Konstruktionszeichnung spricht. Die
 * Umrechnung passiert an genau einer Stelle (`pointDistance`), nicht in jeder
 * Formel.
 */
export interface Point {
  label: string
  x: number
  y: number
  z: number
  /** Der Nullpunkt selbst — nicht verschiebbar. */
  fixed?: boolean
  /** Welche Winkelfreiheit an diesem Punkt sitzt. */
  dof?: DofKey
}

export type PointKey =
  | 'cross' | 'waistD' | 'waistC' | 'chest' | 'pelvis'
  | 'hipG' | 'hipR' | 'hipN' | 'knee' | 'ankle' | 'mtp' | 'sole'
  | 'shoulder' | 'elbow' | 'wrist' | 'headC'

export type PointSet = Record<PointKey, Point>

/* -------------------------------------------------------------------------
   Unsymmetrie
   ------------------------------------------------------------------------- */

/**
 * Punkte, die es links wie rechts gibt.
 *
 * DER PUNKTSATZ FÜHRT EINE ZAHL FÜR BEIDE SEITEN. Das ist der Normalfall und
 * die richtige Vorgabe: ein Zweibeiner ist symmetrisch, und wer den
 * Oberschenkel verlängert, meint beide. Eine Konstruktion, die für jedes Bein
 * eigene Zahlen führt, lädt zu einem Zahlendreher ein, den niemand findet — bis
 * die Maschine hinkt.
 */
export const MIRRORED_POINTS = new Set<PointKey>([
  'hipG',
  'hipR',
  'hipN',
  'knee',
  'ankle',
  'mtp',
  'sole',
  'shoulder',
  'elbow',
  'wrist',
  'waistD',
  'waistC',
])

export const isMirrored = (key: PointKey): boolean => MIRRORED_POINTS.has(key)

/**
 * Die eigenen Masse der LINKEN Seite — nur wo sie von der gemeinsamen abweichen.
 *
 * WOFÜR ES SIE GIBT UND WARUM SIE NICHT DER NORMALFALL IST. Eine gewollte
 * Unsymmetrie ist eine Konstruktionsentscheidung: ein anderer Anlenkpunkt
 * links, ein Bein, das absichtlich kürzer ist. Sie muss darstellbar sein — aber
 * sie darf nicht aus Versehen entstehen. Deshalb ist der Grundfall die
 * gemeinsame Zahl, und eine Seite bekommt erst dann eigene Werte, wenn jemand
 * das ausdrücklich sagt.
 *
 * GEZÄHLT WIRD IN DERSELBEN RICHTUNG wie im Punktsatz. Der Eintrag ist keine
 * gespiegelte Koordinate, sondern das Mass der linken Seite in der Zählweise
 * der rechten: `y = 130` heisst links 130 mm nach aussen, nicht 130 mm nach
 * rechts. Nur so lassen sich die beiden Spalten nebeneinander lesen.
 *
 * TEILWEISE UND NICHT VOLLSTÄNDIG: wer nur die Knieachse links tiefer setzt,
 * bekommt einen Eintrag mit einem `z` und erbt x und y weiter aus dem
 * Punktsatz. Ein vollständiger zweiter Punkt wäre eine Kopie, die beim nächsten
 * Eingriff auf der rechten Seite stehen bliebe.
 */
export type Asymmetry = Partial<Record<PointKey, Partial<Pick<Point, 'x' | 'y' | 'z'>>>>

export type Side = 'R' | 'L'

/**
 * Der Punkt, wie er auf DIESER Seite gilt.
 *
 * Die eine Stelle, an der die Unsymmetrie gelesen wird. Wer stattdessen
 * `model.points[key]` nimmt, bekommt immer die rechte Seite — und merkt es
 * nicht, solange niemand eine Unsymmetrie angelegt hat.
 */
export function pointFor(model: HumanoidModel, key: PointKey, side: Side): Point {
  const point = model.points[key]
  if (side === 'R') return point
  const own = model.asym?.[key]
  return own ? { ...point, ...own } : point
}

/** Welche Achsen dieses Punktes links eigene Werte tragen. */
export function asymAxes(model: HumanoidModel, key: PointKey): ('x' | 'y' | 'z')[] {
  const own = model.asym?.[key]
  if (!own) return []
  return (['x', 'y', 'z'] as const).filter(
    (axis) => own[axis] !== undefined && own[axis] !== model.points[key][axis],
  )
}

/** Trägt dieser Punkt eine gewollte Unsymmetrie? */
export const isAsym = (model: HumanoidModel, key: PointKey): boolean =>
  asymAxes(model, key).length > 0

/** Alle Punkte mit gewollter Unsymmetrie. */
export const asymPoints = (model: HumanoidModel): PointKey[] =>
  (Object.keys(model.asym ?? {}) as PointKey[]).filter((key) => isAsym(model, key))

/** Abstand zweier Punkte auf EINER Seite, in Metern. */
export function sideDistance(
  model: HumanoidModel,
  a: PointKey,
  b: PointKey,
  side: Side,
): number {
  const pa = pointFor(model, a, side)
  const pb = pointFor(model, b, side)
  return Math.hypot(pa.x - pb.x, pa.y - pb.y, pa.z - pb.z) / 1000
}

/* -------------------------------------------------------------------------
   Winkelfreiheiten
   ------------------------------------------------------------------------- */

/**
 * Eine Achse: Bewegungsbereich in Grad und zulässiges Moment am GELENK.
 *
 * Nicht am Motor — zwischen beiden liegt das Getriebe, und dessen Übersetzung
 * ist Ergebnis der Auslegung, nicht ihre Voraussetzung.
 */
export interface AxisFreedom {
  /** Untere Winkelgrenze in Grad. */
  lo: number
  /** Obere Winkelgrenze in Grad. */
  hi: number
  /** Zulässiges Moment der Achse in Nm. */
  moment: number
}

/**
 * Winkelfreiheiten eines Gelenks im Roboterrahmen.
 *
 * Drehung um x = Rollen (seitlich), um y = Nicken (vor/zurück),
 * um z = Gieren (Hochachse). `null` heisst: diese Achse gibt es nicht.
 */
export interface Dof {
  label: string
  x: AxisFreedom | null
  y: AxisFreedom | null
  z: AxisFreedom | null
}

export type DofKey =
  | 'rumpf' | 'hipG' | 'hipR' | 'hipN' | 'knee' | 'ankle'
  | 'mtp' | 'shoulder' | 'elbow' | 'wrist' | 'hals'

export type DofSet = Record<DofKey, Dof>

/** Die drei Achsen in der Reihenfolge, in der sie angezeigt werden. */
export const AXES = [
  { key: 'y', label: 'Nicken', about: 'um die Querachse y' },
  { key: 'x', label: 'Rollen', about: 'um die Längsachse x' },
  { key: 'z', label: 'Gieren', about: 'um die Hochachse z' },
] as const

export type AxisKey = (typeof AXES)[number]['key']

/* -------------------------------------------------------------------------
   Massen
   ------------------------------------------------------------------------- */

export type SegmentKey =
  | 'thigh' | 'shank' | 'foot' | 'upperArm' | 'forearm'
  | 'trunk' | 'head' | 'payload'

/**
 * Eine Masse in KILOGRAMM, mit ihrem Sitz.
 *
 * Absolut und nicht als Anteil der Körpermasse. Das ist der Unterschied
 * zwischen einer Konstruktion und einem Menschmodell: Anteile stammen aus der
 * Anthropometrie, Kilogramm aus der Stückliste. Wer ein Bauteil schwerer macht,
 * will nicht, dass dabei still alle anderen Segmente mitwachsen.
 *
 * Die Anteile gibt es weiter — als abgeleitete Grösse für den Vergleich mit der
 * Literatur.
 */
export interface MassItem {
  label: string
  /** Masse in Kilogramm. Bei paarigen Segmenten je Seite. */
  kg: number
  /** Schwerpunkt vom proximalen Gelenk, Anteil der Segmentlänge. */
  com: number
  /** Trägheitsradius, Anteil der Segmentlänge. */
  gyration: number
  /** Links und rechts vorhanden — zählt doppelt in die Gesamtmasse. */
  paired: boolean
  /**
   * Feste Segmentlänge in Metern, wenn sie sich nicht aus den Punkten ergibt.
   * `null` heisst: aus den Gelenkpunkten abgeleitet — der Normalfall.
   */
  length: number | null
}

export type MassSet = Record<SegmentKey, MassItem>

/* -------------------------------------------------------------------------
   Leistung und Energie
   ------------------------------------------------------------------------- */

/** Auslegungspunkte des Antriebsstrangs. */
export interface Performance {
  /** Höchste Gehgeschwindigkeit, m/s. */
  walkMax: number
  /** Höchste Laufgeschwindigkeit, m/s. */
  runMax: number
  /** Höchste Sprintgeschwindigkeit, m/s. */
  sprintMax: number
  /** Höchste Kadenz, Schritte je Minute. */
  cadenceMax: number
}

/**
 * Rekuperation: was von der Bremsleistung zurückkommt.
 *
 * Je Gelenk ein Speicher — eine Feder in Reihe zum Antrieb beziehungsweise der
 * Zwischenkreis. Von der Bremsleistung erreicht nur `beta` den Speicher, der
 * Rest geht in Dämpfung, Struktur und Wandlerverluste.
 */
export interface Recuperation {
  /** Anteil der Bremsleistung, den die Feder aufnimmt. */
  beta: number
  /** Rückgabewirkungsgrad der Feder. */
  eta: number
  /** Speicherinhalt je Gelenk in Joule. */
  cap: number
  /** Entladezeitkonstante der Feder in Sekunden. */
  tau: number
  /** Motor als Generator auf den Zwischenkreis. */
  gen: number
  /** Zwischenkreis zurück in mechanische Leistung. */
  inv: number
  /** Kupferverlust je Nm² Haltemoment, in W/Nm². */
  copper: number
  /** Grundlast Elektronik, Rechner, Kühlung, in Watt. */
  idle: number
}

/* -------------------------------------------------------------------------
   Das ganze Modell
   ------------------------------------------------------------------------- */

export interface HumanoidModel {
  /** Kennung des Zahlenstands, damit zwei Stände unterscheidbar bleiben. */
  id: string
  label: string
  /**
   * Massstab des Gesamtaufbaus.
   *
   * Bringt den Aufbau auf die Zielbauhöhe. In der Vorlage steckt er in den
   * Längenliteralen (`0.420 / 1.045`); hier steht er getrennt, weil die 420 mm
   * eine belegte Grösse sind und der Massstab eine Darstellungsentscheidung.
   */
  scale: number
  /**
   * Abstand der Ferse hinter dem Sohlenpunkt, in Metern.
   *
   * In der Vorlage ein Zahlenliteral mitten in der Fussgeometrie. Er gehört
   * hierher: ein anderer Fuss darf nicht stillschweigend dieselbe Ferse behalten,
   * und der Wert entscheidet mit, wie lange der Fuss in der Standphase um die
   * Ferse dreht.
   */
  heelOffset: number
  points: PointSet
  /**
   * Gewollte Unsymmetrie — die eigenen Masse der linken Seite.
   *
   * Fehlt sie oder ist sie leer, ist die Maschine symmetrisch. Das ist der
   * Normalfall; siehe `Asymmetry`.
   */
  asym?: Asymmetry
  dofs: DofSet
  masses: MassSet
  /**
   * Vergleichswert für den Massenabgleich, in Kilogramm.
   *
   * `null` heisst: kein Abgleich gefordert. Der Wert wird NIE gerechnet — er
   * wird der Summe gegenübergestellt.
   */
  massTarget: number | null
  performance: Performance
  recuperation: Recuperation
  /** Referenzgang: Normkurven je Gelenk und Ebene. */
  joints: Record<string, JointDef>
}

/* -------------------------------------------------------------------------
   Abgeleitete Grössen — Funktionen, keine gespeicherten Werte
   ------------------------------------------------------------------------- */

/** Abstand zweier Gelenkpunkte in METERN. Die Punkte stehen in Millimetern. */
export function pointDistance(model: HumanoidModel, a: PointKey, b: PointKey): number {
  const p = model.points[a]
  const q = model.points[b]
  return Math.hypot(p.x - q.x, p.y - q.y, p.z - q.z) / 1000
}

/**
 * Segmentlänge in Metern, aus den Gelenkpunkten.
 *
 * Genau das ist der Grund, warum die Punkte die Eingabe sind: wer die Knieachse
 * verschiebt, ändert damit den Oberschenkel — ohne dass irgendwo eine zweite
 * Zahl nachgeführt werden müsste.
 */
export function segmentLength(model: HumanoidModel, key: SegmentKey): number {
  const explicit = model.masses[key].length
  if (explicit !== null) return explicit

  switch (key) {
    case 'thigh':
      return pointDistance(model, 'hipN', 'knee')
    case 'shank':
      return pointDistance(model, 'knee', 'ankle')
    case 'foot':
      return pointDistance(model, 'ankle', 'mtp')
    case 'trunk':
      return pointDistance(model, 'pelvis', 'shoulder')
    case 'upperArm':
      return pointDistance(model, 'shoulder', 'elbow')
    case 'forearm':
      return pointDistance(model, 'elbow', 'wrist')
    default:
      throw new Error(`Segment ${key} hat weder feste Länge noch Herleitung aus den Punkten`)
  }
}

/** Gestreckte Beinlänge, Hüftnickachse bis Sprunggelenkmitte, in Metern. */
export const legLength = (model: HumanoidModel): number =>
  segmentLength(model, 'thigh') + segmentLength(model, 'shank')

/**
 * Bauhöhe: Sohlenpunkt bis Kopfmitte, in Metern.
 *
 * DIE DEFINITION IST DIE HALBE AUSSAGE. Der oberste Punkt des Modells ist die
 * KOPFMITTE, nicht der Scheitel — das Modell führt keinen Scheitelpunkt. Wer
 * die Bauhöhe bis zum Scheitel meint, muss den halben Kopf dazurechnen und
 * bekommt gut 12 cm mehr.
 *
 * Gemessen wird deshalb, was das Modell hergibt, und der Name sagt es. Eine
 * Zahl, die als Scheitelhöhe gelesen wird und keine ist, wäre schlimmer als
 * gar keine.
 */
export const buildHeight = (model: HumanoidModel): number =>
  Math.abs(model.points.headC.z - model.points.sole.z) / 1000

/** Höhe der Hüftnickachse über der Sohle, in Metern. */
export const hipHeight = (model: HumanoidModel): number =>
  Math.abs(model.points.hipN.z - model.points.sole.z) / 1000

/**
 * Eigenmasse ohne Zuladung, in Kilogramm — die SUMME der Einzelmassen.
 *
 * Paarige Segmente zählen doppelt. Es gibt keine getrennt gepflegte
 * Gesamtmasse; wer eine hätte, müsste sie bei jeder Bauteiländerung
 * nachziehen — und würde es irgendwann vergessen.
 */
export function ownMass(model: HumanoidModel): number {
  let sum = 0
  for (const [key, item] of Object.entries(model.masses)) {
    if (key === 'payload') continue
    sum += item.paired ? item.kg * 2 : item.kg
  }
  return sum
}

/** Zuladung in Kilogramm. */
export const payloadMass = (model: HumanoidModel): number => model.masses.payload.kg

/** Massenanteil eines Segments an der Eigenmasse — für den Vergleich mit der Literatur. */
export const massFraction = (model: HumanoidModel, key: SegmentKey): number =>
  model.masses[key].kg / ownMass(model)

/**
 * Massenabgleich gegen den Vergleichswert.
 *
 * Die Abweichung wird gezeigt, nicht verrechnet. Eine Bilanz, die sich selbst
 * korrigiert, verbirgt genau den Fehler, den sie finden soll.
 */
export interface MassCheck {
  sum: number
  target: number | null
  /** Summe minus Vergleichswert, in Kilogramm. `null` ohne Vergleichswert. */
  delta: number | null
  /** Abweichung als Anteil des Vergleichswerts. `null` ohne Vergleichswert. */
  relative: number | null
}

export function massCheck(model: HumanoidModel): MassCheck {
  const sum = ownMass(model)
  const target = model.massTarget
  if (target === null || target === 0) {
    return { sum, target, delta: null, relative: null }
  }
  const delta = sum - target
  return { sum, target, delta, relative: delta / target }
}

/* -------------------------------------------------------------------------
   Grenzen je Gelenk und Ebene — abgeleitet aus den Winkelfreiheiten
   ------------------------------------------------------------------------- */

export interface PlaneLimit {
  lo: number
  hi: number
  moment: number
  source: string
}

export type LimitSet = Record<string, Partial<Record<PlaneKey, PlaneLimit>>>

/**
 * Welche Winkelfreiheit welche Ebene welches Gelenks ist.
 *
 * DIE ÜBERSETZUNG ZWISCHEN ZWEI SPRACHEN, und sie steht genau einmal. Die
 * Konstruktion spricht von Freiheiten um x, y und z an benannten Modulen —
 * `hipR.x`, `knee.y`. Das Gangmodell spricht von Gelenken und Ebenen — `hip`
 * frontal, `knee` sagittal. Vorher lag diese Zuordnung als Literal in
 * `planeLimits` und war damit nur dort abrufbar; das Einfrieren einer
 * stillgelegten Achse braucht sie ebenso, und zwei Tabellen für dieselbe
 * Zuordnung wären zwei Gelegenheiten, sie unterschiedlich zu machen.
 */
export interface DofAxisSpec {
  dof: DofKey
  axis: AxisKey
  /** Gelenk im Gangmodell — der Name, unter dem die Kurve liegt. */
  joint: string
  plane: PlaneKey
  /**
   * Zählt gegenläufig zur Winkelfreiheit.
   *
   * Die Freiheiten zählen positiv nach aussen, die aufgezeichneten Kurven für
   * die Frontalachsen positiv nach innen. Aus `lo … hi` wird `−hi … −lo`. Wer
   * das übersieht, bekommt eine Grenze, die auf der falschen Seite der Kurve
   * liegt, und eine Überschreitung, die keine ist.
   */
  mirror: boolean
  /**
   * Trägt diese Achse eine Grenze in `planeLimits`?
   *
   * Die Grenzen sind der Massstab, an dem die aufgezeichneten Kurven geprüft
   * werden. Für Rumpf, Hals und Handgelenk gibt es keine geprüfte Kurve — eine
   * Grenze dort wäre ein Vergleich gegen nichts. Stilllegen lässt sich der
   * Rumpf trotzdem, und dann steht seine Kurve still.
   */
  limit: boolean
  source: string
}

export const DOF_AXES: readonly DofAxisSpec[] = [
  { dof: 'hipN', axis: 'y', joint: 'hip', plane: 'sag', mirror: false, limit: true,
    source: 'Winkelfreiheiten · Nickmodul' },
  { dof: 'hipR', axis: 'x', joint: 'hip', plane: 'fro', mirror: true, limit: true,
    source: 'Winkelfreiheiten · Rollmodul' },
  { dof: 'hipG', axis: 'z', joint: 'hip', plane: 'tra', mirror: false, limit: true,
    source: 'Winkelfreiheiten · Giermodul' },
  { dof: 'knee', axis: 'y', joint: 'knee', plane: 'sag', mirror: false, limit: true,
    source: 'Winkelfreiheiten · Kurbelschwinge' },
  { dof: 'ankle', axis: 'y', joint: 'ankle', plane: 'sag', mirror: false, limit: true,
    source: 'Winkelfreiheiten · Parallelkinematik' },
  { dof: 'ankle', axis: 'x', joint: 'ankle', plane: 'fro', mirror: true, limit: true,
    source: 'Winkelfreiheiten · Parallelkinematik' },
  /* Kein Zehengrundgelenk: der Gen5-Fuss ist starr. Die Gangkurve des
     Zehengrundgelenks bleibt im Referenzgang, hat aber keine Freiheit, gegen
     die sie zu prüfen wäre — was dort anfällt, trägt die Struktur. */
  { dof: 'shoulder', axis: 'y', joint: 'shoulder', plane: 'sag', mirror: false, limit: true,
    source: 'Winkelfreiheiten · Schulter' },
  { dof: 'shoulder', axis: 'x', joint: 'shoulder', plane: 'fro', mirror: true, limit: true,
    source: 'Winkelfreiheiten · Schulter' },
  { dof: 'shoulder', axis: 'z', joint: 'shoulder', plane: 'tra', mirror: false, limit: true,
    source: 'Winkelfreiheiten · Schulter' },
  { dof: 'elbow', axis: 'y', joint: 'elbow', plane: 'sag', mirror: false, limit: true,
    source: 'Winkelfreiheiten · Ellbogen' },

  /* Der Rumpf. Er trägt keine Grenze — siehe `limit` —, steht aber im
     Gangmodell als Lendenwirbelsäule und lässt sich deshalb stilllegen. */
  { dof: 'rumpf', axis: 'y', joint: 'lumbar', plane: 'sag', mirror: false, limit: false,
    source: 'Winkelfreiheiten · Rumpfaktuator' },
  { dof: 'rumpf', axis: 'x', joint: 'lumbar', plane: 'fro', mirror: true, limit: false,
    source: 'Winkelfreiheiten · Rumpfaktuator' },
]

/**
 * Grenzen der Ebenen aus den Winkelfreiheiten — EIN Satz Zahlen, kein zweiter.
 *
 * Stillgelegte Freiheiten stehen NICHT darin: eine Achse, die auf ihrem
 * Rastwinkel festgehalten wird, hat keinen Bereich mehr. Was sie an Moment
 * trägt, trägt ab dann die Struktur.
 */
export function planeLimits(model: HumanoidModel, off: Iterable<string> = []): LimitSet {
  const stopped = new Set(off)
  const out: LimitSet = {}

  for (const spec of DOF_AXES) {
    if (!spec.limit || stopped.has(spec.dof)) continue
    const a: AxisFreedom | null = model.dofs[spec.dof][spec.axis]
    if (!a) continue
    const planes = out[spec.joint] ?? (out[spec.joint] = {})
    planes[spec.plane] = spec.mirror
      ? { lo: -a.hi, hi: -a.lo, moment: a.moment, source: spec.source }
      : { lo: a.lo, hi: a.hi, moment: a.moment, source: spec.source }
  }

  return out
}
